import { QueryClient } from '@tanstack/react-query';
import createReactQueryClient from 'openapi-react-query';
import { apiClient } from './client';

/**
 * The TanStack Query client. Owns every piece of state that originated
 * on the API side — thought records, sections, feedback, the user
 * object. Anything genuinely client-only lives in Zustand instead (see
 * apps/client/docs/overview.md §"How the client consumes the API").
 *
 * The defaults are tuned for an editor:
 *   - refetchOnWindowFocus: false. The user editing a thought record
 *     does not want focus-blur to silently re-pull the record and
 *     potentially clobber an in-flight optimistic update. Section
 *     state is invalidated explicitly on mutation success.
 *   - staleTime: 30s. Most reads are served from cache during a
 *     session; the explicit invalidations on PUT/DELETE keep the cache
 *     honest where it matters.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * `$api` is the typed React Query hook factory from `openapi-react-query`,
 * built on top of `apiClient`. Components consume it as
 *   const { data } = $api.useQuery('get', '/thought-records/{recordId}', {
 *     params: { path: { recordId: id } },
 *   });
 * with the method, path, params, and `data` shape all enforced by the
 * OpenAPI-derived `paths` type.
 *
 * The `$` prefix is the convention from the openapi-react-query docs;
 * it visually distinguishes the API hook namespace from local hooks.
 */
export const $api = createReactQueryClient(apiClient);
